import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, message } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';
import './ManualJDForm.css';

const { Option } = Select;

const ManualJDForm = ({ visible, onCreate, onCancel, backendURL }) => {
    const [generationAttempts, setGenerationAttempts] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [form] = Form.useForm();

    const MAX_GENERATION_ATTEMPTS = 3;

    const seniorityLevels = [
        { value: 'Entry Trainee', label: 'Entry / Trainee' },
        { value: 'Early Professional', label: 'Early Professional' },
        { value: 'Skilled Practitioner', label: 'Skilled Practitioner' },
        { value: 'Advanced Practitioner', label: 'Advanced Practitioner' },
        { value: 'Expert Consultant', label: 'Expert / Consultant' },
        { value: 'Unit Leader', label: 'Unit Leader' },
        { value: 'Department Lead', label: 'Department Lead' },
        { value: 'Executive Leadership', label: 'Executive Leadership' },
        { value: 'Specialised Practice', label: 'Specialised Practice' },
        { value: 'Education Research', label: 'Education & Research' }
    ];

    // Reset form when modal visibility changes
    useEffect(() => {
        if (!visible) {
            resetForm();
        }
    }, [visible]);

    const resetForm = () => {
        form.resetFields();
        setGenerationAttempts(0);
        setIsGenerating(false);
    };

    const handleGenerateWithAI = async () => {
        if (generationAttempts >= MAX_GENERATION_ATTEMPTS) {
            message.error(
                'You have reached the maximum number of AI generation attempts. Please edit the current description or start over.'
            );
            return;
        }

        try {
            // Only validate the fields needed for AI generation
            await form.validateFields(['companyName', 'jobTitle', 'seniority']);
            const formValues = form.getFieldsValue();
            setIsGenerating(true);

            // Create the base payload
            const requestPayload = {
                company: formValues.companyName,
                jobTitle: formValues.jobTitle,
                seniority: formValues.seniority,
            };

            // Add additionalContext only if aiInstructions exists and isn't empty
            const trimmedInstructions = formValues.aiInstructions?.trim();
            if (trimmedInstructions) {
                requestPayload.additionalContext = trimmedInstructions;
            }

            console.log(requestPayload);

            const response = await axios.post(`${backendURL}/v1/generate-jd`, requestPayload);

            if (response.data.description) {
                setGenerationAttempts(prev => prev + 1);

                form.setFieldsValue({
                    jobDescription: response.data.description
                });

                const remainingAttempts = MAX_GENERATION_ATTEMPTS - (generationAttempts + 1);
                if (remainingAttempts > 0) {
                    message.success(
                        `Job description generated successfully. You have ${remainingAttempts} generation attempts remaining.`
                    );
                } else {
                    message.warning(
                        'Job description generated successfully. This was your last generation attempt.'
                    );
                }
            }

        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in the required company name, job title, and seniority level');
            } else {
                console.error('Failed to generate job description:', error);
                message.error('Failed to generate job description. Please try again.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSubmit = () => {
        // Validate all fields including job description when submitting
        form.validateFields()
            .then(values => {
                onCreate(values);
                resetForm();
            })
            .catch(info => {
                console.log('Validation Failed:', info);
            });
    };

    const handleCancel = () => {
        resetForm();
        onCancel();
    };

    return (
        <Modal
            open={visible}
            title={
                <div>
                    <FileTextOutlined className="modal-title-icon" />
                    Enter Job Details
                </div>
            }
            onCancel={handleCancel}
            className="modern-jd-modal"
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    key="generateAI"
                    className="modern-ai-btn"
                    onClick={handleGenerateWithAI}
                    loading={isGenerating}
                    disabled={generationAttempts >= MAX_GENERATION_ATTEMPTS}
                >
                    {generationAttempts >= MAX_GENERATION_ATTEMPTS
                        ? 'Generation Limit Reached'
                        : `Generate with AI`
                    }
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                >
                    Submit
                </Button>,
            ]}
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
                className="modern-jd-form"
                initialValues={{
                    jobTitle: '',
                    companyName: '',
                    seniority: undefined,
                    aiInstructions: '',
                    jobDescription: ''
                }}
            >
                <Form.Item
                    name="jobTitle"
                    label="Job Title"
                    rules={[{ required: true, message: 'Please enter the job title' }]}
                >
                    <Input placeholder="e.g., Software Engineer" />
                </Form.Item>

                <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true, message: 'Please enter the company name' }]}
                >
                    <Input placeholder="e.g., RecruitmentX, AI Consulting Group, Microsoft etc" />
                </Form.Item>

                <Form.Item
                    name="seniority"
                    label="Seniority Level"
                    rules={[{ required: true, message: 'Please select the seniority level' }]}
                >
                    <Select placeholder="Select seniority level">
                        {seniorityLevels.map(level => (
                            <Option key={level.value} value={level.value}>
                                {level.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="aiInstructions"
                    label="Additional Instructions for AI"
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="e.g., must have experience with cloud platforms, strong emphasis on team collaboration, etc."
                    />
                </Form.Item>

                <Form.Item
                    name="jobDescription"
                    label="Job Description"
                    rules={[{
                        required: true,
                        message: 'Please enter the job description or use AI generation',
                        validator: (_, value) => {
                            // Only validate when submitting the form
                            if (!value && form.isFieldTouched('jobDescription')) {
                                return Promise.reject('Job description is required');
                            }
                            return Promise.resolve();
                        }
                    }]}
                >
                    <Input.TextArea
                        rows={4}
                        placeholder="Enter detailed job description or use AI generation"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ManualJDForm;